/**
 * Responsive Theme Overrides for Tablet/Touch Devices
 *
 * This module provides MUI component style overrides that are applied
 * via CSS media queries to optimize the UI for touch devices.
 *
 * Key principles:
 * - Touch targets meet WCAG 2.2 minimum of 24x24px (we use 44px)
 * - Font sizes prevent iOS zoom on input focus (16px minimum)
 * - Spacing is increased for finger-friendly interactions
 *
 * @see https://www.w3.org/WAI/WCAG22/Understanding/target-size-minimum.html
 * @see https://developer.apple.com/design/human-interface-guidelines/accessibility
 * @see https://m3.material.io/foundations/accessible-design/accessibility-basics
 */

import { Components, Theme } from '@mui/material/styles';
import {
  MEDIA_QUERIES,
  RESPONSIVE_SIZES,
  TOUCH_TARGET_MIN_SIZE
} from '../constants/responsive';
import { colors } from '../constants/colors';
import { transparentBorder, standardBorder } from '../constants/styles';

/**
 * Generate responsive component overrides for tablet/touch devices.
 * These overrides are merged with the base theme component styles.
 *
 * @param _primaryColor - The primary color for the current module theme (reserved for future use)
 * @returns MUI Components overrides with responsive styles
 */
export function getResponsiveOverrides(
  _primaryColor: string
): Components<Omit<Theme, 'components'>> {
  const touchQuery = MEDIA_QUERIES.touch;

  return {
    /**
     * Button - Increase touch target and font size
     *
     * We use 40px as the default for buttons (slightly less than 44px)
     * to balance touch accessibility with visual density in headers/toolbars.
     * This still meets WCAG 2.2 minimum of 24px and is close to Apple's 44pt.
     */
    MuiButton: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            minHeight: 40, // Balanced touch target for general use
            fontSize: `${RESPONSIVE_SIZES.fontSize.tablet}px`,
            padding: '8px 16px'
          }
        },
        sizeSmall: {
          [touchQuery]: {
            minHeight: 36, // Compact but still touch-friendly
            padding: '6px 12px',
            fontSize: '14px'
          }
        },
        sizeLarge: {
          [touchQuery]: {
            minHeight: TOUCH_TARGET_MIN_SIZE, // Full 44px for large buttons
            padding: '10px 24px'
          }
        }
      }
    },

    /**
     * IconButton - Ensure adequate touch target
     * Note: We only set minHeight to avoid horizontal overflow issues
     */
    MuiIconButton: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            minHeight: TOUCH_TARGET_MIN_SIZE,
            padding: 10,
            // Maintain border styling from base theme
            border: transparentBorder,
            '&:hover': {
              backgroundColor: colors.canvas.light,
              border: standardBorder
            }
          }
        },
        sizeSmall: {
          [touchQuery]: {
            minHeight: 36,
            padding: 6
          }
        },
        sizeLarge: {
          [touchQuery]: {
            minHeight: 52,
            padding: 14
          }
        }
      }
    },

    /**
     * OutlinedInput - Increase height and font size
     *
     * Also increases width for small inputs (xs, xxs) to accommodate
     * the increased padding on touch devices, preventing placeholder
     * text from being cut off.
     */
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            minHeight: TOUCH_TARGET_MIN_SIZE,
            // Increase width for small inputs to accommodate touch padding
            // xs: 80px -> 95px, xxs: 50px -> 65px
            '&[style*="width: 50px"]': {
              width: '65px !important'
            },
            '&[style*="width: 80px"]': {
              width: '95px !important'
            }
          }
        },
        input: {
          [touchQuery]: {
            padding: '12px 14px',
            fontSize: `${RESPONSIVE_SIZES.fontSize.tablet}px`
          }
        },
        multiline: {
          [touchQuery]: {
            '& > textarea': {
              padding: '12px 14px'
            }
          }
        }
      }
    },

    /**
     * InputBase - Base input styles
     */
    MuiInputBase: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            fontSize: `${RESPONSIVE_SIZES.fontSize.tablet}px`
          }
        },
        input: {
          [touchQuery]: {
            fontSize: `${RESPONSIVE_SIZES.fontSize.tablet}px`
          }
        }
      }
    },

    /**
     * Checkbox - Larger touch target and checkbox size
     */
    MuiCheckbox: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            padding: RESPONSIVE_SIZES.padding.tablet,
            '& svg': {
              width: `${RESPONSIVE_SIZES.checkboxSize.tablet}px`,
              height: `${RESPONSIVE_SIZES.checkboxSize.tablet}px`
            }
          }
        }
      }
    },

    /**
     * Radio - Larger touch target and radio size
     */
    MuiRadio: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            padding: RESPONSIVE_SIZES.padding.tablet,
            '& svg': {
              width: `${RESPONSIVE_SIZES.checkboxSize.tablet}px`,
              height: `${RESPONSIVE_SIZES.checkboxSize.tablet}px`
            }
          }
        }
      }
    },

    /**
     * Switch - Larger touch target
     */
    MuiSwitch: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            padding: RESPONSIVE_SIZES.padding.tablet,
            width: 58, // Wider for easier toggling
            height: 38
          }
        },
        switchBase: {
          [touchQuery]: {
            padding: 9
          }
        }
      }
    },

    /**
     * MenuItem - Larger touch target for dropdowns/menus
     */
    MuiMenuItem: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            minHeight: TOUCH_TARGET_MIN_SIZE,
            paddingTop: RESPONSIVE_SIZES.padding.tablet,
            paddingBottom: RESPONSIVE_SIZES.padding.tablet,
            fontSize: `${RESPONSIVE_SIZES.fontSize.tablet}px`
          }
        }
      }
    },

    /**
     * ListItem - Larger touch target for list items
     */
    MuiListItem: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            minHeight: TOUCH_TARGET_MIN_SIZE,
            paddingTop: RESPONSIVE_SIZES.padding.tablet,
            paddingBottom: RESPONSIVE_SIZES.padding.tablet
          }
        }
      }
    },

    /**
     * ListItemButton - Larger touch target for clickable list items
     */
    MuiListItemButton: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            minHeight: TOUCH_TARGET_MIN_SIZE,
            paddingTop: RESPONSIVE_SIZES.padding.tablet,
            paddingBottom: RESPONSIVE_SIZES.padding.tablet
          }
        }
      }
    },

    /**
     * Tab - Larger touch target for tab navigation
     */
    MuiTab: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            minHeight: TOUCH_TARGET_MIN_SIZE,
            // Don't set minWidth to allow tabs to shrink on smaller screens
            padding: '12px 16px',
            fontSize: `${RESPONSIVE_SIZES.fontSize.tablet}px`
          }
        }
      }
    },

    /**
     * Chip - Larger size for touch interaction
     */
    MuiChip: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            height: RESPONSIVE_SIZES.chipHeight.tablet,
            fontSize: `${RESPONSIVE_SIZES.fontSize.tablet - 2}px` // Slightly smaller
          }
        },
        deleteIcon: {
          [touchQuery]: {
            width: 22,
            height: 22,
            margin: '0 4px 0 -4px'
          }
        },
        label: {
          [touchQuery]: {
            paddingLeft: 12,
            paddingRight: 12
          }
        }
      }
    },

    /**
     * SvgIcon - Larger icons for better visibility
     */
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            fontSize: `${RESPONSIVE_SIZES.iconSize.tablet}px`
          }
        },
        fontSizeSmall: {
          [touchQuery]: {
            fontSize: `${RESPONSIVE_SIZES.iconSize.tablet - 4}px`
          }
        },
        fontSizeLarge: {
          [touchQuery]: {
            fontSize: `${RESPONSIVE_SIZES.iconSize.tablet + 8}px`
          }
        }
      }
    },

    /**
     * FormControlLabel - Larger touch target for label + control
     */
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            minHeight: TOUCH_TARGET_MIN_SIZE,
            marginLeft: 0
          }
        },
        label: {
          [touchQuery]: {
            fontSize: `${RESPONSIVE_SIZES.fontSize.tablet}px`,
            marginLeft: 8
          }
        }
      }
    },

    /**
     * Select - Larger touch target for select dropdowns
     */
    MuiSelect: {
      styleOverrides: {
        select: {
          [touchQuery]: {
            minHeight: `${TOUCH_TARGET_MIN_SIZE - 24}px`, // Account for padding
            paddingTop: 12,
            paddingBottom: 12,
            fontSize: `${RESPONSIVE_SIZES.fontSize.tablet}px`
          }
        },
        icon: {
          [touchQuery]: {
            fontSize: '1.5rem',
            right: 8
          }
        }
      }
    },

    /**
     * Autocomplete - Larger input for autocomplete
     */
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          [touchQuery]: {
            minHeight: TOUCH_TARGET_MIN_SIZE,
            fontSize: `${RESPONSIVE_SIZES.fontSize.tablet}px`
          }
        },
        option: {
          [touchQuery]: {
            minHeight: TOUCH_TARGET_MIN_SIZE,
            padding: '12px 16px',
            fontSize: `${RESPONSIVE_SIZES.fontSize.tablet}px`
          }
        },
        clearIndicator: {
          [touchQuery]: {
            padding: 8
          }
        },
        popupIndicator: {
          [touchQuery]: {
            padding: 8
          }
        }
      }
    },

    /**
     * TableCell - Larger padding for touch-friendly rows
     */
    MuiTableCell: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            padding: '12px 16px',
            fontSize: `${RESPONSIVE_SIZES.fontSize.tablet}px`
          }
        },
        head: {
          [touchQuery]: {
            padding: '14px 16px'
          }
        }
      }
    },

    /**
     * AccordionSummary - Larger touch target for expandable sections
     */
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            minHeight: TOUCH_TARGET_MIN_SIZE + 8 // 52px
          }
        },
        expandIconWrapper: {
          [touchQuery]: {
            padding: 8
          }
        }
      }
    },

    /**
     * Pagination - Larger touch targets for page buttons
     */
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            minWidth: 36, // Smaller than full touch target to prevent overflow
            minHeight: 36,
            fontSize: `${RESPONSIVE_SIZES.fontSize.tablet}px`
          }
        }
      }
    },

    /**
     * Breadcrumbs - Larger touch targets for navigation
     */
    MuiBreadcrumbs: {
      styleOverrides: {
        li: {
          [touchQuery]: {
            '& a, & button': {
              minHeight: 36,
              padding: '4px 8px',
              fontSize: `${RESPONSIVE_SIZES.fontSize.tablet}px`
            }
          }
        }
      }
    },

    /**
     * ToggleButton - Larger touch target
     */
    MuiToggleButton: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            minHeight: TOUCH_TARGET_MIN_SIZE,
            // Don't set minWidth to allow buttons to fit in available space
            padding: '8px 12px'
          }
        }
      }
    },

    /**
     * Slider - Larger thumb for easier dragging
     */
    MuiSlider: {
      styleOverrides: {
        thumb: {
          [touchQuery]: {
            width: 24,
            height: 24
          }
        },
        rail: {
          [touchQuery]: {
            height: 6
          }
        },
        track: {
          [touchQuery]: {
            height: 6
          }
        }
      }
    },

    /**
     * Fab (Floating Action Button) - Already touch-friendly but ensure minimum
     */
    MuiFab: {
      styleOverrides: {
        root: {
          [touchQuery]: {
            minWidth: 56,
            minHeight: 56
          }
        },
        sizeSmall: {
          [touchQuery]: {
            minWidth: TOUCH_TARGET_MIN_SIZE,
            minHeight: TOUCH_TARGET_MIN_SIZE
          }
        }
      }
    },

    /**
     * SpeedDial - Larger touch targets for speed dial actions
     */
    MuiSpeedDialAction: {
      styleOverrides: {
        fab: {
          [touchQuery]: {
            width: TOUCH_TARGET_MIN_SIZE,
            height: TOUCH_TARGET_MIN_SIZE
          }
        }
      }
    }
  };
}
